import { CHIP_COLORS } from "../components/basic-reports/utils/colors";

// --- Types ---

export interface MeterDataRow {
  "Meter Id": string;
  Timestamp: number;
  Voltage: number;
  Frequency: number;
  NeutralCurrent: number;
  PowerFactor: number;
  kW: number;
  kVA: number;
  kWh: number;
  kWhExp: number;
  kVAh: number;
  kVAhExp: number;
  MDkW: number;
  MDkVA: number;
  MDResetCount: number;
  ProgramCount: number;
  TamperCount: number;
  TotalPowerOnDuration: number;
  RelayStatus: number;
  LoadLimitValue: number;
  Interval_kWh: number;
  Interval_kWhExp: number;
  Interval_kVAh: number;
  Tamper_Delta: number;
  Reset_Delta: number;
  Program_Delta: number;
  Relay_Change: number;
  Connected_Count: number;
  Disconnected_Count: number;
  Load_Violation: number;
  Low_PF: number;
  UnderVolt: number;
  OverVolt: number;
  ReverseFlow: number;
  "Hourly Curve": number;
  EventCode?: string;
  EventDescription?: string;
  isActive?: boolean;
}

export interface PerMeterOperations {
  meterId: string;
  status: "Online" | "Offline";
  connectedReadings: number;
  disconnectedReadings: number;
  relayChanges: number;
  uptimePercent: number;
  totalPowerOnHours: number;
  lastReading: string;
}

export interface EnrichedMeterData extends MeterDataRow {
  dateObj: Date;
}

// --- Telemetry API Response Types ---

export interface TelemetryEntry {
  ts: number;
  value: string;
}

export type TelemetryApiResponse = Record<string, TelemetryEntry[]>;

export interface DeviceEntry {
  id: { id: string; entityType: string };
  name: string;
  label?: string;
}

// --- Per-Meter Aggregation Types ---

export interface PerMeterEnergy {
  meterId: string;
  totalKWh: number;
  totalKWhExp: number;
  totalKVAh: number;
  peakKW: number;
  avgKW: number;
  readingCount: number;
}

export interface PerMeterDemand {
  meterId: string;
  maxKW: number;
  maxKVA: number;
  avgKW: number;
  peakTime: string;
  mdResets: number;
  loadFactor: number;
}

export interface PerMeterQuality {
  meterId: string;
  avgVoltage: number;
  minVoltage: number;
  maxVoltage: number;
  avgFrequency: number;
  avgPF: number;
  underVoltEvents: number;
  overVoltEvents: number;
  lowPFEvents: number;
  status: string;
}

export interface PerMeterTamper {
  meterId: string;
  tamperEvents: number;
  resetEvents: number;
  programChanges: number;
  reverseFlowEvents: number;
  loadViolations: number;
  lastEvent: string;
  severity: string;
}

export interface EventEntry {
  timestamp: string;
  meterId: string;
  eventCode: string;
  eventDescription: string;
}

export interface DailyDataPoint {
  date: string;
  value: number;
  label: string;
}

export interface HourlyDataPoint {
  hour: string;
  value: number;
  label: string;
}

// --- API Response Transformer ---

export const transformApiResponse = (
  meterId: string,
  meterName: string,
  apiData: TelemetryApiResponse,
  isActive?: boolean,
): EnrichedMeterData[] => {
  if (!apiData || Object.keys(apiData).length === 0) return [];

  const maxRows = Math.max(
    ...Object.values(apiData).map((arr) => arr?.length || 0),
  );
  if (maxRows === 0) return [];

  const getVal = (key: string, index: number): number => {
    const entry = apiData[key]?.[index];
    return entry ? parseFloat(entry.value) || 0 : 0;
  };

  const getStr = (key: string, index: number): string => {
    return apiData[key]?.[index]?.value || "";
  };

  const getTs = (index: number): number => {
    // Find the first available timestamp from any key
    for (const key of Object.keys(apiData)) {
      if (apiData[key]?.[index]?.ts) return apiData[key][index].ts;
    }
    return 0;
  };

  const parseRelayStatus = (val: any): number | null => {
    if (val === undefined || val === null || String(val).trim() === "")
      return null;
    const s = String(val).toLowerCase();
    if (s === "closed" || s === "connected" || s === "1" || s === "1.0")
      return 1;
    if (s === "open" || s === "disconnected" || s === "0" || s === "0.0")
      return 0;

    // If it's a number (for aggregated counts), return it as is
    const num = parseFloat(s);
    if (!isNaN(num)) return num;

    return null;
  };

  // 1. Extract Raw Data
  let rawData: any[] = [];
  for (let i = 0; i < maxRows; i++) {
    const ts = getTs(i);
    if (ts === 0) continue;

    rawData.push({
      i,
      ts,
      "Meter Id": meterName,
      Timestamp: ts,
      kWh: getVal("IP_KWH", i),
      kWhExp: getVal("IP_KWHEXP", i),
      kVAh: getVal("IP_KVAH", i),
      kW: getVal("IP_KW", i),
      kVA: getVal("IP_KVA", i),
      kVAhExp: getVal("IP_KVAHEXP", i),
      Voltage: getVal("IP_Voltage", i),
      Frequency: getVal("IP_Frequency", i),
      NeutralCurrent: getVal("IP_NeutralCurrent", i),
      PowerFactor: getVal("IP_PowerFactor", i),
      MDkW: getVal("IP_MDKW", i),
      MDkVA: getVal("IP_MDKVA", i),
      LoadLimitValue: getVal("IP_LoadLimitValue", i),
      TamperCount: getVal("IP_TamperCount", i),
      MDResetCount: getVal("IP_MDResetCount", i),
      ProgramCount: getVal("IP_ProgramCount", i),
      TotalPowerOnDuration: getVal("IP_TotalPowerOnDuration", i),
      RelayStatus: parseRelayStatus(apiData["IP_RelayStatus"]?.[i]?.value),
      Relay_Change:
        getVal("IP_Relay_Change", i) ||
        getVal("IP_RelayChange", i) ||
        getVal("IP_RelayChangeCount", i) ||
        0,
      Connected_Count:
        getVal("IP_ConnectedCount", i) || getVal("IP_Connected_Count", i) || 0,
      Disconnected_Count:
        getVal("IP_DisconnectedCount", i) ||
        getVal("IP_Disconnected_Count", i) ||
        0,
      tamperDeltaApi: getVal("IP_Tamper_Delta", i) || 0,
      resetDeltaApi: getVal("IP_Reset_Delta", i) || 0,
      programDeltaApi: getVal("IP_Program_Delta", i) || 0,
      EventCode: getStr("EventCode", i),
      EventDescription: getStr("EventDescription", i),
      isActive,
    });
  }

  // 2. Sort by Timestamp (Oldest to Newest)
  rawData.sort((a, b) => a.ts - b.ts);

  // 3. Compute Deltas and Build Final Rows
  const rows: EnrichedMeterData[] = [];

  rawData.forEach((curr, idx) => {
    const prev = idx > 0 ? rawData[idx - 1] : curr;

    // Use API deltas if available, fallback to manual diff
    const intervalKWh = Math.max(0, curr.kWh - prev.kWh);
    const intervalKWhExp = Math.max(0, curr.kWhExp - prev.kWhExp);
    const intervalKVAh = Math.max(0, curr.kVAh - prev.kVAh);

    // Prioritize API-provided change counts or summation
    // If Connected_Count is provided, use it. If RelayStatus > 1, it's likely a count from HES
    const isCommunicating = curr.Voltage > 0;
    const connectedCount =
      curr.Connected_Count ||
      (curr.RelayStatus === 1 ||
      (curr.RelayStatus !== null && curr.RelayStatus > 1)
        ? curr.RelayStatus || 1
        : curr.RelayStatus === null && isCommunicating
          ? 1
          : 0);

    const disconnectedCount =
      curr.Disconnected_Count || (curr.RelayStatus === 0 ? 1 : 0);

    const relayChange =
      curr.Relay_Change ||
      (prev &&
      curr.RelayStatus !== null &&
      prev.RelayStatus !== null &&
      curr.RelayStatus !== prev.RelayStatus
        ? 1
        : 0);

    const tamperDelta =
      curr.tamperDeltaApi || Math.max(0, curr.tamperCount - prev.tamperCount);
    const resetDelta =
      curr.resetDeltaApi || Math.max(0, curr.mdResetCount - prev.mdResetCount);
    const programDelta =
      curr.programDeltaApi ||
      Math.max(0, curr.programCount - prev.programCount);

    // Status Flags
    const loadViolation =
      curr.LoadLimitValue > 0 && curr.kW > curr.LoadLimitValue ? 1 : 0;
    const lowPF = curr.PowerFactor > 0 && curr.PowerFactor < 0.9 ? 1 : 0;
    const underVolt = curr.Voltage > 0 && curr.Voltage < 210 ? 1 : 0;
    const overVolt = curr.Voltage > 250 ? 1 : 0;
    const reverseFlow = intervalKWhExp > 0 ? 1 : 0;

    rows.push({
      "Meter Id": meterName || meterId,
      Timestamp: curr.ts,
      Voltage: curr.Voltage,
      Frequency: curr.Frequency,
      NeutralCurrent: curr.NeutralCurrent,
      PowerFactor: curr.PowerFactor,
      kW: curr.kW,
      kVA: curr.kVA,
      kWh: curr.kWh,
      kWhExp: curr.kWhExp,
      kVAh: curr.kVAh,
      kVAhExp: curr.kVAhExp,
      MDkW: curr.MDkW,
      MDkVA: curr.MDkVA,
      MDResetCount: curr.MDResetCount,
      ProgramCount: curr.ProgramCount,
      TamperCount: curr.TamperCount,
      TotalPowerOnDuration: curr.TotalPowerOnDuration,
      RelayStatus: curr.RelayStatus,
      LoadLimitValue: curr.LoadLimitValue,
      Interval_kWh: intervalKWh,
      Interval_kWhExp: intervalKWhExp,
      Interval_kVAh: intervalKVAh,
      Tamper_Delta: tamperDelta,
      Reset_Delta: resetDelta,
      Program_Delta: programDelta,
      Relay_Change: relayChange,
      Connected_Count: connectedCount,
      Disconnected_Count: disconnectedCount,
      Load_Violation: loadViolation,
      Low_PF: lowPF,
      UnderVolt: underVolt,
      OverVolt: overVolt,
      ReverseFlow: reverseFlow,
      "Hourly Curve": intervalKWh,
      EventCode: curr.EventCode,
      EventDescription: curr.EventDescription,
      dateObj: new Date(curr.ts),
      isActive: curr.isActive,
    });
  });

  return rows;
};

// --- Formatting Helpers ---

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNum = (v: number, decimals = 2) =>
  isNaN(v) || !isFinite(v) ? 0 : parseFloat(v.toFixed(decimals));

// --- Group-by Helpers ---

/** Group data by Meter Id */
const groupByMeter = (
  data: EnrichedMeterData[],
): Record<string, EnrichedMeterData[]> => {
  const groups: Record<string, EnrichedMeterData[]> = {};
  data.forEach((row) => {
    const key = row["Meter Id"];
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  });
  return groups;
};

/** Group data by day (YYYY-MM-DD) */
const groupByDay = (
  data: EnrichedMeterData[],
): Record<string, EnrichedMeterData[]> => {
  const groups: Record<string, EnrichedMeterData[]> = {};
  data.forEach((row) => {
    const key = row.dateObj.toISOString().split("T")[0];
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  });
  return groups;
};

// --- Per-Meter Aggregation Functions ---

export const getPerMeterEnergy = (
  data: EnrichedMeterData[],
): PerMeterEnergy[] => {
  const byMeter = groupByMeter(data);
  return Object.entries(byMeter).map(([meterId, rows]) => {
    const totalKWh = rows.reduce((s, r) => s + r.Interval_kWh, 0);
    const totalKWhExp = rows.reduce((s, r) => s + r.Interval_kWhExp, 0);
    const totalKVAh = rows.reduce((s, r) => s + r.Interval_kVAh, 0);
    const peakKW = Math.max(...rows.map((r) => r.kW));
    const avgKW = rows.reduce((s, r) => s + r.kW, 0) / rows.length;
    return {
      meterId,
      totalKWh: formatNum(totalKWh),
      totalKWhExp: formatNum(totalKWhExp),
      totalKVAh: formatNum(totalKVAh),
      peakKW: formatNum(peakKW),
      avgKW: formatNum(avgKW),
      readingCount: rows.length,
    };
  });
};

export const getPerMeterDemand = (
  data: EnrichedMeterData[],
): PerMeterDemand[] => {
  const byMeter = groupByMeter(data);
  return Object.entries(byMeter).map(([meterId, rows]) => {
    let maxKW = 0;
    let maxKVA = 0;
    let peakTime = "";
    rows.forEach((r) => {
      if (r.MDkW > maxKW) {
        maxKW = r.MDkW;
        peakTime = r.dateObj.toLocaleString();
      }
      if (r.MDkVA > maxKVA) maxKVA = r.MDkVA;
    });
    const avgKW = rows.reduce((s, r) => s + r.kW, 0) / rows.length;
    const mdResets = rows.reduce((s, r) => s + r.Reset_Delta, 0);
    const loadFactor = maxKW > 0 ? avgKW / maxKW : 0;
    return {
      meterId,
      maxKW: formatNum(maxKW),
      maxKVA: formatNum(maxKVA),
      avgKW: formatNum(avgKW),
      peakTime: peakTime || "N/A",
      mdResets: Math.round(mdResets),
      loadFactor: formatNum(loadFactor),
    };
  });
};

export const getPerMeterQuality = (
  data: EnrichedMeterData[],
): PerMeterQuality[] => {
  const byMeter = groupByMeter(data);
  return Object.entries(byMeter).map(([meterId, rows]) => {
    const voltages = rows.map((r) => r.Voltage).filter((v) => v > 0);
    const frequencies = rows.map((r) => r.Frequency).filter((v) => v > 0);
    const pfs = rows.map((r) => r.PowerFactor).filter((v) => v > 0);
    const avgVoltage =
      voltages.length > 0
        ? voltages.reduce((s, v) => s + v, 0) / voltages.length
        : 0;
    const minVoltage = voltages.length > 0 ? Math.min(...voltages) : 0;
    const maxVoltage = voltages.length > 0 ? Math.max(...voltages) : 0;
    const avgFrequency =
      frequencies.length > 0
        ? frequencies.reduce((s, v) => s + v, 0) / frequencies.length
        : 0;
    const avgPF =
      pfs.length > 0 ? pfs.reduce((s, v) => s + v, 0) / pfs.length : 0;
    const underVoltEvents = rows.filter((r) => r.UnderVolt > 0).length;
    const overVoltEvents = rows.filter((r) => r.OverVolt > 0).length;
    const lowPFEvents = rows.filter((r) => r.Low_PF > 0).length;
    const hasIssues = underVoltEvents + overVoltEvents + lowPFEvents > 0;
    return {
      meterId,
      avgVoltage: formatNum(avgVoltage, 1),
      minVoltage: formatNum(minVoltage, 0),
      maxVoltage: formatNum(maxVoltage, 0),
      avgFrequency: formatNum(avgFrequency, 2),
      avgPF: formatNum(avgPF, 2),
      underVoltEvents,
      overVoltEvents,
      lowPFEvents,
      status: hasIssues ? "warning" : "good",
    };
  });
};

export const getPerMeterOperations = (
  data: EnrichedMeterData[],
  meterStatuses: Record<string, boolean> = {},
): PerMeterOperations[] => {
  const byMeter = groupByMeter(data);

  // Use all meters from fleet if statuses provided, otherwise just those with telemetry
  const allMeterIds =
    Object.keys(meterStatuses).length > 0
      ? Object.keys(meterStatuses)
      : Object.keys(byMeter);

  return allMeterIds.map((meterId) => {
    const rows = byMeter[meterId] || [];
    const connected =
      rows.reduce((s, r) => s + (r.Connected_Count || 0), 0) ||
      rows.reduce(
        (s, r) =>
          s + (r.RelayStatus === 1 ? 1 : r.RelayStatus > 1 ? r.RelayStatus : 0),
        0,
      );
    const disconnected =
      rows.reduce((s, r) => s + (r.Disconnected_Count || 0), 0) ||
      rows.reduce((s, r) => s + (r.RelayStatus === 0 ? 1 : 0), 0);
    const validReadings = rows.filter((r) => r.Voltage > 0).length;
    const relayChanges = rows.reduce((s, r) => s + (r.Relay_Change || 0), 0);
    const uptimePercent =
      rows.length > 0 ? (validReadings / rows.length) * 100 : 0;
    const lastRow = rows[rows.length - 1];
    const lastPowerOn = lastRow?.TotalPowerOnDuration || 0;

    // Get status from fleet map, fallback to check any telemetry row if available
    const isActuallyActive =
      meterStatuses[meterId] ?? rows[0]?.isActive ?? false;

    return {
      meterId: meterId, // Keep raw ID/Name for component-level formatting
      status: isActuallyActive ? "Online" : "Offline",
      connectedReadings: connected,
      disconnectedReadings: disconnected,
      relayChanges: Math.round(relayChanges),
      uptimePercent: formatNum(uptimePercent, 1),
      lastReading: lastRow?.dateObj.toLocaleString() || "N/A",
      totalPowerOnHours: formatNum(lastPowerOn / 3600, 1),
    };
  });
};

export const getPerMeterTamper = (
  data: EnrichedMeterData[],
): PerMeterTamper[] => {
  const byMeter = groupByMeter(data);
  return Object.entries(byMeter).map(([meterId, rows]) => {
    const tamperEvents = rows.reduce((s, r) => s + r.Tamper_Delta, 0);
    const resetEvents = rows.reduce((s, r) => s + r.Reset_Delta, 0);
    const programChanges = rows.reduce((s, r) => s + r.Program_Delta, 0);
    const reverseFlowEvents = rows.filter((r) => r.ReverseFlow > 0).length;
    const loadViolations = rows.filter((r) => r.Load_Violation > 0).length;
    const lastTamperRow = [...rows]
      .reverse()
      .find((r) => r.Tamper_Delta > 0 || r.Reset_Delta > 0);
    const total = tamperEvents + resetEvents + loadViolations;
    const severity = total > 5 ? "high" : total > 0 ? "medium" : "low";
    return {
      meterId,
      tamperEvents: Math.round(tamperEvents),
      resetEvents: Math.round(resetEvents),
      programChanges: Math.round(programChanges),
      reverseFlowEvents,
      loadViolations,
      lastEvent: lastTamperRow?.dateObj.toLocaleString() || "None",
      severity,
    };
  });
};

// --- Daily / Hourly Aggregation ---

export const getDailyAggregation = (
  data: EnrichedMeterData[],
  metricFn: (rows: EnrichedMeterData[]) => number,
): DailyDataPoint[] => {
  const byDay = groupByDay(data);
  return Object.entries(byDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, rows]) => {
      const d = new Date(date);
      const label = d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      return {
        date,
        value: formatNum(metricFn(rows)),
        label,
      };
    });
};

export const getHourlyAggregation = (
  data: EnrichedMeterData[],
  metricFn: (rows: EnrichedMeterData[]) => number,
): HourlyDataPoint[] => {
  const byHour: Record<number, EnrichedMeterData[]> = {};
  data.forEach((row) => {
    const hour = row.dateObj.getHours();
    if (!byHour[hour]) byHour[hour] = [];
    byHour[hour].push(row);
  });
  return Array.from({ length: 24 }, (_, i) => ({
    hour: `${String(i).padStart(2, "0")}:00`,
    value: byHour[i] ? formatNum(metricFn(byHour[i])) : 0,
    label: `${String(i).padStart(2, "0")}:00`,
  }));
};

// --- Event Extraction ---

export const extractEvents = (data: EnrichedMeterData[]): EventEntry[] => {
  return data
    .filter((r) => r.EventCode && r.EventCode !== "" && r.EventCode !== "0")
    .map((r) => ({
      timestamp: r.dateObj.toLocaleString(),
      meterId: r["Meter Id"],
      eventCode: r.EventCode || "",
      eventDescription: r.EventDescription || "Unknown Event",
    }));
};

// --- Global Aggregation Functions (used by context) ---

export const aggregateEnergyData = (data: EnrichedMeterData[]) => {
  const totalKWh = data.reduce((sum, row) => sum + (row.Interval_kWh || 0), 0);
  const totalKVAh = data.reduce(
    (sum, row) => sum + (row.Interval_kVAh || 0),
    0,
  );
  const totalKWhExp = data.reduce(
    (sum, row) => sum + (row.Interval_kWhExp || 0),
    0,
  );

  const hourlyChartData = getHourlyAggregation(data, (rows) =>
    rows.reduce((s, r) => s + r.Interval_kWh, 0),
  ).map((d) => ({ time: d.hour, kwh: Math.round(d.value) }));

  // Per-meter energy for distribution
  const perMeter = getPerMeterEnergy(data);
  const totalMeterKWh = perMeter.reduce((s, m) => s + m.totalKWh, 0);

  const distribution = perMeter.slice(0, 10).map((m, i) => ({
    name: m.meterId,
    value:
      totalMeterKWh > 0 ? formatNum((m.totalKWh / totalMeterKWh) * 100, 1) : 0,
    kwh: m.totalKWh,
    color: [
      CHIP_COLORS.good,
      CHIP_COLORS.pending,
      CHIP_COLORS.high,
      CHIP_COLORS.info,
      CHIP_COLORS.warning,
      CHIP_COLORS.success,
      CHIP_COLORS.error,
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
    ][i % 10],
  }));

  const dailyConsumption = getDailyAggregation(data, (rows) =>
    rows.reduce((s, r) => s + r.Interval_kWh, 0),
  );

  return {
    totalKWh: formatNum(totalKWh),
    totalKVAh: formatNum(totalKVAh),
    totalKWhExp: formatNum(totalKWhExp),
    hourlyChartData,
    distribution,
    dailyConsumption,
    perMeter,
  };
};

export const analyzePowerQuality = (data: EnrichedMeterData[]) => {
  const totalReadings = data.length;
  const lowVoltageCount = data.filter((r) => r.UnderVolt > 0).length;
  const highVoltageCount = data.filter((r) => r.OverVolt > 0).length;

  // Full voltage/frequency profile for charts
  const voltageProfile = data.map((row) => ({
    time: row.dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    voltage: row.Voltage,
    frequency: row.Frequency,
    powerFactor: row.PowerFactor,
    meterId: row["Meter Id"],
  }));

  const perMeter = getPerMeterQuality(data);

  const voltages = data.map((r) => r.Voltage).filter((v) => v > 0);
  const frequencies = data.map((r) => r.Frequency).filter((v) => v > 0);
  const pfs = data.map((r) => r.PowerFactor).filter((v) => v > 0);

  return {
    metrics: {
      avgVoltage:
        voltages.length > 0
          ? formatNum(voltages.reduce((s, v) => s + v, 0) / voltages.length, 1)
          : 0,
      minVoltage: voltages.length > 0 ? Math.min(...voltages) : 0,
      maxVoltage: voltages.length > 0 ? Math.max(...voltages) : 0,
      avgFrequency:
        frequencies.length > 0
          ? formatNum(
              frequencies.reduce((s, v) => s + v, 0) / frequencies.length,
              2,
            )
          : 0,
      avgPF:
        pfs.length > 0
          ? formatNum(pfs.reduce((s, v) => s + v, 0) / pfs.length, 2)
          : 0,
      sagSwellEvents: lowVoltageCount + highVoltageCount,
      lowVoltageCount,
      highVoltageCount,
      lowPFCount: data.filter((r) => r.Low_PF > 0).length,
      totalReadings,
    },
    voltageProfile,
    perMeter,
  };
};

export const calculatePeakDemand = (data: EnrichedMeterData[]) => {
  let peakKW = 0;
  let peakKVA = 0;
  let peakTime = "";

  data.forEach((row) => {
    if (row.MDkW > peakKW) {
      peakKW = row.MDkW;
      peakTime = row.dateObj.toLocaleString();
    }
    if (row.MDkVA > peakKVA) peakKVA = row.MDkVA;
  });

  const avgKW =
    data.length > 0 ? data.reduce((s, r) => s + r.kW, 0) / data.length : 0;
  const loadFactor = peakKW > 0 ? avgKW / peakKW : 0;

  // Demand trend using all data points
  const demandTrend = data.map((row) => ({
    time: row.dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    kw: row.kW,
    kva: row.kVA,
    mdkw: row.MDkW,
    timestamp: row.dateObj.toLocaleString(),
    meterId: row["Meter Id"],
  }));

  // Daily peak demand
  const dailyPeak = getDailyAggregation(data, (rows) =>
    Math.max(...rows.map((r) => r.kW)),
  );
  const dailyAvg = getDailyAggregation(
    data,
    (rows) => rows.reduce((s, r) => s + r.kW, 0) / rows.length,
  );

  // Hourly load factor
  const hourlyLoadFactor = getHourlyAggregation(data, (rows) => {
    const hPeak = Math.max(...rows.map((r) => r.kW));
    const hAvg = rows.reduce((s, r) => s + r.kW, 0) / rows.length;
    return hPeak > 0 ? hAvg / hPeak : 0;
  });

  // Contract load from max LoadLimitValue
  const loadLimitValues = data
    .map((r) => r.LoadLimitValue)
    .filter((v) => v > 0);
  const contractLoad =
    loadLimitValues.length > 0 ? Math.max(...loadLimitValues) : 0;

  const violations = data.filter((r) => r.Load_Violation > 0).length;

  const perMeter = getPerMeterDemand(data);

  return {
    peakKW: formatNum(peakKW),
    peakKVA: formatNum(peakKVA),
    peakTime: peakTime || new Date().toISOString(),
    avgKW: formatNum(avgKW),
    loadFactor: formatNum(loadFactor, 3),
    contractLoad: formatNum(contractLoad),
    violations,
    demandTrend,
    dailyPeak,
    dailyAvg,
    hourlyLoadFactor,
    perMeter,
  };
};

export const analyzeOperations = (
  data: EnrichedMeterData[],
  totalConnected = 0,
  totalMeters = 0,
  meterStatuses: Record<string, boolean> = {},
) => {
  const byMeter = groupByMeter(data);

  // SUM based metrics instead of meter counting
  const totalConnectedReads = data.reduce(
    (s, r) =>
      s +
      (r.Connected_Count ||
        (r.RelayStatus === 1 ? 1 : r.RelayStatus > 1 ? r.RelayStatus : 0)),
    0,
  );
  const totalDisconnectedReads = data.reduce(
    (s, r) => s + (r.Disconnected_Count || (r.RelayStatus === 0 ? 1 : 0)),
    0,
  );
  const totalRelayChanges = data.reduce((s, r) => s + (r.Relay_Change || 0), 0);
  const totalReadings = data.length;

  const total = totalMeters > 0 ? totalMeters : Object.keys(byMeter).length;

  // Uptime based on successful communication (Voltage > 0) instead of just RelayStatus
  const validIntervals = data.filter((r) => r.Voltage > 0).length;
  const uptimePercent =
    data.length > 0 ? (validIntervals / data.length) * 100 : 0;

  const connectivityTrend = getHourlyAggregation(data, (rows) => {
    // Return count of unique meters that were active in this hour
    const hourMeters = groupByMeter(rows);
    return Object.keys(hourMeters).filter((mid) => {
      // mid is already the meter name here from groupByMeter
      return meterStatuses[mid] ?? hourMeters[mid][0].isActive;
    }).length;
  });

  const disconnTrend = getHourlyAggregation(data, (rows) => {
    const hourMeters = groupByMeter(rows);
    return Object.keys(hourMeters).filter((mid) => {
      return !(meterStatuses[mid] ?? hourMeters[mid][0].isActive);
    }).length;
  });

  const hourlyConnectivity = connectivityTrend.map((c, i) => ({
    time: c.hour,
    connected: c.value,
    disconnected: Math.max(
      0,
      (totalMeters || c.value + (disconnTrend[i]?.value || 0)) - c.value,
    ),
    label: c.label,
  }));

  // Daily relay activity
  const dailyConnects = getDailyAggregation(data, (rows) =>
    rows.reduce(
      (s, r) => s + (r.Relay_Change > 0 && r.RelayStatus === 1 ? 1 : 0),
      0,
    ),
  );
  const dailyDisconnects = getDailyAggregation(data, (rows) =>
    rows.reduce(
      (s, r) => s + (r.Relay_Change > 0 && r.RelayStatus === 0 ? 1 : 0),
      0,
    ),
  );

  // Communication analysis by hour
  const communicationStats = getHourlyAggregation(data, (rows) => {
    const validReadings = rows.filter((r) => r.Voltage > 0).length;
    return rows.length > 0 ? (validReadings / rows.length) * 100 : 0;
  });

  const perMeter = getPerMeterOperations(data, meterStatuses);
  const events = extractEvents(data);

  return {
    currentStatus: {
      connected: totalConnectedReads,
      disconnected: totalDisconnectedReads,
      total,
      relayChanges: Math.round(totalRelayChanges),
      uptimePercent: formatNum(uptimePercent, 1),
    },
    hourlyConnectivity,
    dailyConnects,
    dailyDisconnects,
    communicationStats,
    perMeter,
    events,
  };
};

export const analyzeTamper = (data: EnrichedMeterData[]) => {
  const tamperEvents = data.reduce((s, r) => s + (r.Tamper_Delta || 0), 0);
  const resetEvents = data.reduce((s, r) => s + (r.Reset_Delta || 0), 0);
  const programChanges = data.reduce((s, r) => s + (r.Program_Delta || 0), 0);
  const reverseEnergy = data.filter((r) => r.ReverseFlow > 0).length;
  const loadViolations = data.filter((r) => r.Load_Violation > 0).length;

  // Events from EventCode/EventDescription
  const events = extractEvents(data);

  // Event type breakdown from real events
  const eventTypeCounts: Record<string, number> = {};
  events.forEach((e) => {
    const type = e.eventDescription || e.eventCode || "Unknown";
    eventTypeCounts[type] = (eventTypeCounts[type] || 0) + 1;
  });

  const eventColors = [
    CHIP_COLORS.failed,
    CHIP_COLORS.warning,
    CHIP_COLORS.info,
    CHIP_COLORS.low,
    CHIP_COLORS.pending,
    "#8B5CF6",
  ];

  const eventsByType =
    Object.keys(eventTypeCounts).length > 0
      ? Object.entries(eventTypeCounts).map(([type, count], i) => ({
          type,
          count,
          color: eventColors[i % eventColors.length],
        }))
      : [
          // Fallback: use computed deltas if no event codes
          {
            type: "Tamper Events",
            count: Math.round(tamperEvents),
            color: CHIP_COLORS.failed,
          },
          {
            type: "MD Resets",
            count: Math.round(resetEvents),
            color: CHIP_COLORS.warning,
          },
          {
            type: "Program Changes",
            count: Math.round(programChanges),
            color: CHIP_COLORS.info,
          },
          {
            type: "Reverse Flow",
            count: reverseEnergy,
            color: CHIP_COLORS.low,
          },
          {
            type: "Load Violations",
            count: loadViolations,
            color: CHIP_COLORS.pending,
          },
        ].filter((e) => e.count > 0);

  // Check for non-zero deltas
  const culprit = data.find(
    (r) => (r.Tamper_Delta || 0) + (r.Reset_Delta || 0) > 0,
  );

  // Daily tamper trend
  const dailyTamperTrend = getDailyAggregation(data, (rows) =>
    rows.reduce((s, r) => {
      // Sum all relevant deltas to match "Event Breakdown" totals
      const totalDelta =
        (r.Tamper_Delta || 0) + (r.Reset_Delta || 0) + (r.Program_Delta || 0);

      // If there's an event code but no delta recorded, count as 1 event
      const hasEventOnly =
        r.EventCode &&
        r.EventCode !== "0" &&
        r.EventCode !== "" &&
        totalDelta === 0;

      return s + totalDelta + (hasEventOnly ? 1 : 0);
    }, 0),
  );

  const perMeter = getPerMeterTamper(data);

  return {
    metrics: {
      tamperEvents: Math.round(tamperEvents),
      resetEvents: Math.round(resetEvents),
      programChanges: Math.round(programChanges),
      reverseEnergy,
      loadViolations,
      anomalies: data.filter((r) => r.Load_Violation > 0 || r.ReverseFlow > 0)
        .length,
    },
    eventsByType,
    dailyTamperTrend,
    perMeter,
    events,
  };
};
